import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { useState, useMemo, Fragment } from 'react';
import { ArrowLeft, RefreshCw, Trash2, ChevronRight, ChevronDown } from 'lucide-react';
import { holdingReconApi, reconApi } from '@/shared/api';
import { IsinRow } from '../../module-1-portfolio-dp-recon/components/TxnReconView';

type StatusFilter = 'all' | 'mismatch' | 'only_pf' | 'only_dp' | 'match';

function fmtNum(n: number | null | undefined): string {
  if (n == null) return '—';
  return n.toLocaleString('en-IN');
}

function SummaryCard({
  label, value, tone,
}: { label: string; value: string | number | null | undefined; tone: 'neutral' | 'match' | 'warn' | 'mismatch' | 'info' }) {
  const colours = {
    neutral:  { color: 'var(--text-strong)' },
    match:    { color: 'var(--status-ok)' },
    warn:     { color: 'var(--status-warn)' },
    mismatch: { color: 'var(--status-err)' },
    info:     { color: 'var(--status-info)' },
  };
  const len = String(value ?? '—').length;
  const sizeCls = len > 12 ? 'text-lg' : len > 9 ? 'text-xl' : 'text-2xl';
  return (
    <div className="card p-4 min-h-[84px] flex flex-col justify-between">
      <div className="text-[10px] uppercase tracking-wider text-axis-ash mb-1">{label}</div>
      <div className={`${sizeCls} font-semibold tabular-nums break-words leading-tight`} style={colours[tone]} title={String(value ?? '—')}>
        {value ?? '—'}
      </div>
    </div>
  );
}

function Chip({ label, count, active, onClick }: {
  label: string; count: number; active: boolean; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition ${
        active
          ? 'bg-axis-maroon text-white'
          : 'bg-axis-cloud-100 text-axis-ink hover:bg-axis-cloud-200'
      }`}
    >
      {label}
      <span className={`inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] font-bold ${
        active ? 'bg-white/20 text-white' : 'bg-axis-cloud-200 text-axis-ash'
      }`}>{count}</span>
    </button>
  );
}

export function HoldingReconDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['holding-recon-job', id],
    queryFn: () => holdingReconApi.get(id!),
    enabled: !!id,
  });

  const sourceReconJobId = (data as any)?.source_recon_job_id ?? null;

  // Fetch the source transaction recon job so we can show the per-ISIN
  // transaction detail inline when a holding row is expanded.
  const { data: sourceReconData } = useQuery({
    queryKey: ['recon-job-for-holding', sourceReconJobId],
    queryFn: () => reconApi.get(sourceReconJobId!),
    enabled: !!sourceReconJobId,
  });

  // Map canonical_isin → { row, pairs } from the source recon job.
  const isinDetailMap = useMemo(() => {
    const map = new Map<string, { row: any; pairs: any[] }>();
    if (!sourceReconData) return map;
    const pairsByRow = new Map<string, any[]>();
    for (const p of (sourceReconData as any).txn_pairs ?? []) {
      const list = pairsByRow.get(p.row_id) ?? [];
      list.push(p);
      pairsByRow.set(p.row_id, list);
    }
    for (const r of (sourceReconData as any).rows ?? []) {
      map.set(r.canonical_isin, { row: r, pairs: pairsByRow.get(r.id) ?? [] });
    }
    return map;
  }, [sourceReconData]);

  const [expandedIsin, setExpandedIsin] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: () => holdingReconApi.delete(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['holding-recon-jobs'] });
      const src = (data as any)?.source_recon_job_id;
      navigate(src ? `/recon/${src}` : '/recon');
    },
  });

  const summary = (data as any)?.summary ?? null;
  const allRows: any[] = (data as any)?.rows ?? [];

  const counts = useMemo(() => ({
    all:      allRows.length,
    mismatch: allRows.filter(r => r.status === 'MISMATCH').length,
    only_pf:  allRows.filter(r => r.status === 'ONLY_IN_PF').length,
    only_dp:  allRows.filter(r => r.status === 'ONLY_IN_DP').length,
    match:    allRows.filter(r => r.status === 'MATCH').length,
  }), [allRows]);

  const filtered = useMemo(() => {
    let rows = allRows;
    if (filter === 'mismatch') rows = rows.filter(r => r.status === 'MISMATCH');
    else if (filter === 'only_pf') rows = rows.filter(r => r.status === 'ONLY_IN_PF');
    else if (filter === 'only_dp') rows = rows.filter(r => r.status === 'ONLY_IN_DP');
    else if (filter === 'match')   rows = rows.filter(r => r.status === 'MATCH');
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(r =>
        r.isin?.toLowerCase().includes(q) || r.name?.toLowerCase().includes(q),
      );
    }
    return rows;
  }, [allRows, filter, search]);

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="card px-6 py-10 text-center text-axis-ash">Loading…</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="card px-6 py-10 text-center text-axis-ash">Job not found.</div>
      </div>
    );
  }

  const statusBadge = (status: string) => {
    const map: Record<string, string> = {
      completed:       'pill bg-emerald-100 text-emerald-800 border border-emerald-200',
      awaiting_review: 'pill bg-amber-100 text-amber-800 border border-amber-200',
      failed:          'pill bg-rose-100 text-rose-800 border border-rose-200',
      queued:          'pill bg-axis-cloud-100 text-axis-ash border border-axis-cloud-200',
      processing:      'pill bg-blue-100 text-blue-800 border border-blue-200',
    };
    return map[status] ?? 'pill bg-axis-cloud-100 text-axis-ash border border-axis-cloud-200';
  };

  const rowStatusLabel = (s: string) => {
    if (s === 'MATCH')      return 'Match';
    if (s === 'MISMATCH')   return 'Mismatch';
    if (s === 'ONLY_IN_PF') return 'Only in PF';
    if (s === 'ONLY_IN_DP') return 'Only in DP';
    return s;
  };

  const rowStatusPillCls = (s: string) => {
    if (s === 'MATCH')      return 'pill bg-emerald-100 text-emerald-800 border border-emerald-200';
    if (s === 'MISMATCH')   return 'pill bg-rose-100 text-rose-800 border border-rose-200';
    return 'pill bg-amber-100 text-amber-800 border border-amber-200';
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => {
              const src = (data as any)?.source_recon_job_id;
              navigate(src ? `/recon/${src}` : '/recon');
            }}
            className="text-axis-ash hover:text-axis-ink transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-axis-ink">Holding Recon</h1>
              <span className={statusBadge((data as any).status)}>{(data as any).status}</span>
            </div>
            <p className="text-xs text-axis-ash mt-0.5 font-mono">{(data as any).id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button" className="btn-secondary"
            onClick={() => refetch()} disabled={isFetching}
          >
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => {
              if (confirm(`Delete this holding recon job?\n\nThis removes all results permanently.`)) {
                deleteMutation.mutate();
              }
            }}
            disabled={deleteMutation.isPending}
            className="inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg transition disabled:opacity-50"
            style={{
              background: 'color-mix(in srgb, var(--status-err) 8%, transparent)',
              color: 'var(--status-err)',
              border: '1px solid color-mix(in srgb, var(--status-err) 30%, transparent)',
            }}
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>

      {/* ── Filenames ── */}
      {((data as any).pf_filename || (data as any).dp_filename) && (
        <div className="flex gap-4 text-xs text-axis-ash">
          {(data as any).pf_filename && <span>PF: <span className="font-medium text-axis-ink">{(data as any).pf_filename}</span></span>}
          {(data as any).dp_filename && <span>DP: <span className="font-medium text-axis-ink">{(data as any).dp_filename}</span></span>}
        </div>
      )}

      {/* ── Warnings ── */}
      {(summary?.pf_warnings?.length > 0 || summary?.dp_warnings?.length > 0) && (
        <div className="card p-4 bg-amber-50 border-amber-200 text-sm text-amber-800 space-y-1">
          {summary.pf_warnings?.map((w: string, i: number) => <div key={`pf-${i}`}>⚠ PF: {w}</div>)}
          {summary.dp_warnings?.map((w: string, i: number) => <div key={`dp-${i}`}>⚠ DP: {w}</div>)}
        </div>
      )}

      {/* ── Summary tiles row 1: IDs + counts ── */}
      {summary && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <SummaryCard label="PF Client ID"  value={summary.pf_client_id ?? '—'} tone="neutral" />
            <SummaryCard label="DP Client ID"  value={summary.dp_client_id ?? '—'} tone="neutral" />
            <SummaryCard label="ISINs"         value={counts.all}                   tone="neutral" />
            <SummaryCard label="Match rate"    value={`${summary.match_rate_pct?.toFixed(1)}%`} tone={summary.match_rate_pct >= 95 ? 'match' : summary.match_rate_pct >= 70 ? 'warn' : 'mismatch'} />
            <SummaryCard label="Matched"       value={summary.matched}              tone="match" />
            <SummaryCard label="Mismatch"      value={summary.mismatch_count}       tone={summary.mismatch_count > 0 ? 'mismatch' : 'neutral'} />
            <SummaryCard label="Only in PF"    value={summary.only_in_pf}           tone={summary.only_in_pf > 0 ? 'warn' : 'neutral'} />
            <SummaryCard label="Only in DP"    value={summary.only_in_dp}           tone={summary.only_in_dp > 0 ? 'warn' : 'neutral'} />
          </div>

          {/* ── Summary tiles row 2: totals ── */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="card p-4">
              <div className="text-[10px] uppercase tracking-wider text-axis-ash mb-1">Portfolio Total</div>
              <div className="text-3xl font-semibold tabular-nums" style={{ color: 'var(--text-strong)' }}>
                {fmtNum(summary.pf_total_qty)}
              </div>
            </div>
            <div className="card p-4">
              <div className="text-[10px] uppercase tracking-wider text-axis-ash mb-1">DP Total</div>
              <div className="text-3xl font-semibold tabular-nums" style={{ color: 'var(--text-strong)' }}>
                {fmtNum(summary.dp_total_qty)}
              </div>
            </div>
            <div className="card p-4">
              <div className="text-[10px] uppercase tracking-wider text-axis-ash mb-1">Total Difference (PF – DP)</div>
              <div className={`text-3xl font-semibold tabular-nums ${
                Math.abs(summary.total_difference ?? 0) < 0.001
                  ? 'text-emerald-600' : 'text-rose-500'
              }`}>
                {summary.total_difference > 0 ? '+' : ''}{fmtNum(summary.total_difference)}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Filter chips + search ── */}
      <div className="flex flex-wrap items-center gap-2">
        <Chip label="All ISINs"  count={counts.all}      active={filter === 'all'}      onClick={() => setFilter('all')} />
        <Chip label="Mismatch"   count={counts.mismatch} active={filter === 'mismatch'} onClick={() => setFilter('mismatch')} />
        <Chip label="Only in PF" count={counts.only_pf}  active={filter === 'only_pf'}  onClick={() => setFilter('only_pf')} />
        <Chip label="Only in DP" count={counts.only_dp}  active={filter === 'only_dp'}  onClick={() => setFilter('only_dp')} />
        <Chip label="Matched"    count={counts.match}    active={filter === 'match'}     onClick={() => setFilter('match')} />

        {/* ── Trans Recon / Holding Recon view switcher ── */}
        {/* Holding Recon is active (red) here. Trans Recon is white; clicking it
            navigates back to the source transaction recon job. */}
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            disabled={!(data as any).source_recon_job_id}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold transition disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: '#ffffff',
              color: '#000',
              border: '1px solid #e11d48',
            }}
            onClick={() => {
              const src = (data as any).source_recon_job_id;
              if (src) navigate(`/recon/${src}`);
            }}
            title={(data as any).source_recon_job_id
              ? 'Go to the transaction recon for this job'
              : 'No source transaction recon linked to this holding recon'}
          >
            Trans Recon
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold transition"
            style={{
              background: '#e11d48',
              color: '#000',
              border: '1px solid #e11d48',
            }}
            title="Holding Recon (current view)"
          >
            Holding Recon
          </button>

          <div>
            <input
              type="text"
              placeholder="Search ISIN or name…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="text-sm px-3 py-1.5 rounded-md border border-axis-cloud-200 bg-white focus:outline-none focus:border-axis-maroon"
            />
          </div>
        </div>
      </div>

      {/* ── Results table ── */}
      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-axis-cloud-100 text-axis-ash text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">ISIN</th>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-right px-4 py-3">Open Qty (PF)</th>
              <th className="text-right px-4 py-3">Total Qty (DP)</th>
              <th className="text-right px-4 py-3">Difference</th>
              <th className="text-left px-4 py-3">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-axis-cloud-200">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-axis-ash">
                  {search ? 'No results match your search.' : 'No rows.'}
                </td>
              </tr>
            ) : filtered.map((row: any, i: number) => {
              const diff: number = row.difference ?? ((row.pf_qty ?? 0) - (row.dp_qty ?? 0));
              const isMatch = Math.abs(diff) < 0.001 && row.status === 'MATCH';
              const detail = isinDetailMap.get(row.isin);
              const hasDetail = !!detail;
              const isOpen = expandedIsin === row.isin;
              return (
                <Fragment key={row.isin ?? i}>
                  <tr
                    onClick={() => hasDetail && setExpandedIsin(isOpen ? null : row.isin)}
                    className={`${hasDetail ? 'cursor-pointer' : ''} hover:bg-axis-cloud-50 ${
                      row.status === 'MISMATCH' ? 'bg-rose-50/40' :
                      row.status === 'ONLY_IN_PF' || row.status === 'ONLY_IN_DP' ? 'bg-amber-50/30' : ''
                    }`}
                  >
                    <td className="px-4 py-3 font-mono text-xs">
                      <span className="inline-flex items-center gap-1.5">
                        {hasDetail && (isOpen
                          ? <ChevronDown className="w-3.5 h-3.5 text-axis-ash" />
                          : <ChevronRight className="w-3.5 h-3.5 text-axis-ash" />)}
                        {row.isin}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs truncate max-w-[200px]" title={row.name}>{row.name || '—'}</td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {row.pf_qty != null ? fmtNum(row.pf_qty) : <span className="text-axis-ash">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">
                      {row.dp_qty != null ? fmtNum(row.dp_qty) : <span className="text-axis-ash">—</span>}
                    </td>
                    <td className={`px-4 py-3 text-right tabular-nums font-semibold ${
                      isMatch ? 'text-emerald-700' : diff > 0 ? 'text-rose-600' : 'text-amber-700'
                    }`}>
                      {isMatch ? '0' : diff > 0 ? `+${fmtNum(diff)}` : fmtNum(diff)}
                    </td>
                    <td className="px-4 py-3">
                      <span className={rowStatusPillCls(row.status)}>
                        {rowStatusLabel(row.status)}
                      </span>
                    </td>
                  </tr>
                  {isOpen && detail && (
                    <tr>
                      <td colSpan={6} className="p-0 bg-axis-cloud-50/50">
                        <div className="px-4 py-3 border-t border-axis-cloud-200">
                          <div className="text-[11px] uppercase tracking-wider text-axis-ash mb-2">
                            Transaction Recon — {row.isin} {row.name ? `· ${row.name}` : ''}
                          </div>
                          <div className="card overflow-hidden">
                            <IsinRow row={detail.row} pairs={detail.pairs} displayName={row.name} />
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

    </div>
  );
}
